<template>
  <div class="main-data" v-loading="loading">
    <div class="page-header">
      <div class="page-header__title">
        <span>Okrug mahallalari</span>
        <el-tag v-if="okrugTitle" size="large" type="warning">{{ okrugTitle }}</el-tag>
        <el-tag v-if="isActive === false" size="large" type="info">Faol emas</el-tag>
      </div>
      <el-button type="info" @click="$router.push({ name: 'DeputyOkrugList' })">
        Orqaga
      </el-button>
    </div>

    <div class="form-section">
      <div class="form-section__title">Mahalla biriktirish</div>

      <el-row :gutter="10">
        <el-col :xs="24" :sm="24" :md="8" :lg="8" :xl="8">
          <div class="field-label">Viloyat</div>
          <el-select
            v-model="addForm.region_id"
            style="width: 100%"
            size="large"
            placeholder="Viloyatni tanlang"
            :loading="regionsLoading"
            @change="onChangeRegion"
          >
            <el-option
              v-for="region in regions"
              :key="region.id"
              :value="region.id"
              :label="region.title"
            />
          </el-select>
        </el-col>

        <el-col :xs="24" :sm="24" :md="8" :lg="8" :xl="8">
          <div class="field-label">Tuman/Shahar</div>
          <el-select
            v-model="addForm.district_id"
            style="width: 100%"
            size="large"
            placeholder="Tumanni tanlang"
            :loading="districtsLoading"
            @change="onChangeDistrict"
          >
            <el-option
              v-for="district in districts"
              :key="district.id"
              :value="district.id"
              :label="district.title"
            />
          </el-select>
        </el-col>

        <el-col :xs="24" :sm="24" :md="8" :lg="8" :xl="8">
          <div class="field-label">Mahallalar</div>
          <el-select
            v-model="addForm.quarter_ids"
            multiple
            filterable
            style="width: 100%"
            size="large"
            placeholder="Mahallalarni tanlang"
            :loading="quartersLoading"
          >
            <el-option
              v-for="quarter in quarterOptions"
              :key="quarter.id"
              :value="quarter.id"
              :label="quarter.title"
              :disabled="isAlreadyLinked(quarter.id)"
            >
              <span>{{ quarter.title }}</span>
              <span v-if="isAlreadyLinked(quarter.id)" class="linked-hint">
                Biriktirilgan
              </span>
            </el-option>
          </el-select>
        </el-col>
      </el-row>

      <div class="section-actions">
        <el-button
          type="primary"
          plain
          :disabled="!addForm.quarter_ids.length"
          :loading="linking"
          @click="linkQuarters"
        >
          Qo'shish
        </el-button>
      </div>
    </div>

    <div class="form-section">
      <div class="form-section__title">Biriktirilgan mahallalar</div>

      <template v-if="quarters.length">
        <div
          v-for="region in quarters"
          :key="region.region_id"
          class="group"
        >
          <div class="group__region">{{ region.region_title }}</div>

          <div
            v-for="district in region.districts"
            :key="district.district_id"
            class="group__district"
          >
            <div class="group__district-title">{{ district.district_title }}</div>

            <div class="quarter-tags">
              <el-tag
                v-for="quarter in district.quarters"
                :key="quarter.quarter_id"
                closable
                size="large"
                class="quarter-tag"
                @close="unlinkQuarter(quarter)"
              >
                {{ quarter.quarter_title }}
                <span v-if="quarter.citizen_count" class="quarter-tag__count">
                  ({{ formatNumber(quarter.citizen_count) }} aholi)
                </span>
              </el-tag>
            </div>
          </div>
        </div>
      </template>

      <el-empty v-else-if="!loading" description="Mahalla biriktirilmagan" />
    </div>
  </div>
</template>

<script>
import { ElMessageBox, ElNotification } from "element-plus";

export default {
  name: "OkrugQuarters",
  data() {
    return {
      loading: false,
      linking: false,
      regionsLoading: false,
      districtsLoading: false,
      quartersLoading: false,
      okrugTitle: this.$route.query.title || "",
      isActive: null,
      quarters: [],
      regions: [],
      districts: [],
      quarterOptions: [],
      addForm: {
        region_id: null,
        district_id: null,
        quarter_ids: [],
      },
    };
  },
  computed: {
    okrugId() {
      return this.$route.params.id;
    },
    linkedQuarterIds() {
      const ids = [];
      this.quarters.forEach((region) => {
        (region.districts || []).forEach((district) => {
          (district.quarters || []).forEach((quarter) => {
            ids.push(quarter.quarter_id);
          });
        });
      });
      return ids;
    },
  },
  methods: {
    formatNumber(value) {
      return new Intl.NumberFormat("uz-UZ").format(value);
    },
    isAlreadyLinked(quarterId) {
      return this.linkedQuarterIds.includes(quarterId);
    },
    getOkrug() {
      this.loading = true;
      this.$http
        .get(`api/v1/deputyv2/okrugs/${this.okrugId}`)
        .then(({ data }) => {
          this.okrugTitle = data.title || this.okrugTitle;
          this.isActive = data.is_active;
          this.quarters = data.quarters || [];
        })
        .catch(({ response }) => {
          ElNotification({
            type: "error",
            title: "Xatolik",
            message: response?.data?.message || "Yuklashda xatolik sodir bo'ldi",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getRegions() {
      this.regionsLoading = true;
      this.$http
        .get("api/v1/regions")
        .then(({ data }) => {
          this.regions = data.regions || [];
        })
        .catch(() => {})
        .finally(() => {
          this.regionsLoading = false;
        });
    },
    onChangeRegion() {
      this.addForm.district_id = null;
      this.addForm.quarter_ids = [];
      this.districts = [];
      this.quarterOptions = [];
      if (!this.addForm.region_id) return;

      this.districtsLoading = true;
      this.$http
        .get("api/v1/districts", { params: { region_id: this.addForm.region_id } })
        .then(({ data }) => {
          this.districts = data.districts || [];
        })
        .catch(() => {})
        .finally(() => {
          this.districtsLoading = false;
        });
    },
    onChangeDistrict() {
      this.addForm.quarter_ids = [];
      this.quarterOptions = [];
      if (!this.addForm.district_id) return;

      this.quartersLoading = true;
      this.$http
        .get("api/v1/quarters", { params: { district_id: this.addForm.district_id } })
        .then(({ data }) => {
          this.quarterOptions = data.quarters || [];
        })
        .catch(({ response }) => {
          ElNotification({
            type: "error",
            title: "Xatolik",
            message: response?.data?.message || "Mahallalarni yuklashda xatolik sodir bo'ldi",
          });
        })
        .finally(() => {
          this.quartersLoading = false;
        });
    },
    linkQuarters() {
      const ids = this.addForm.quarter_ids.filter((id) => !this.isAlreadyLinked(id));
      if (!ids.length) return;

      this.linking = true;
      const requests = ids.map((quarterId) =>
        this.$http
          .post(`api/v1/deputyv2/okrugs/${this.okrugId}/quarters/${quarterId}`)
          .then(() => ({ quarterId, ok: true }))
          .catch((error) => ({
            quarterId,
            ok: false,
            message: error?.response?.data?.message,
            code: error?.response?.data?.specific_slug,
          }))
      );

      Promise.all(requests)
        .then((results) => {
          const failed = results.filter((item) => !item.ok);
          const succeeded = results.length - failed.length;

          if (succeeded) {
            ElNotification({
              type: "success",
              title: "Tabriklaymiz",
              message: `${succeeded} ta mahalla biriktirildi`,
            });
          }
          failed.forEach((item) => {
            const title = this.quarterTitle(item.quarterId);
            ElNotification({
              type: "error",
              title: "Xatolik",
              message:
                item.code === "QUARTER_ALREADY_LINKED"
                  ? `"${title}" allaqachon boshqa okrugga biriktirilgan`
                  : item.message || `"${title}" biriktirilmadi`,
            });
          });

          this.addForm.quarter_ids = [];
          this.getOkrug();
        })
        .finally(() => {
          this.linking = false;
        });
    },
    quarterTitle(quarterId) {
      return (
        this.quarterOptions.find((item) => item.id === quarterId)?.title ||
        `#${quarterId}`
      );
    },
    unlinkQuarter(quarter) {
      ElMessageBox.confirm(
        `"${quarter.quarter_title}" mahallasini okrugdan uzasizmi?`,
        "Diqqat!",
        {
          confirmButtonText: "Ha",
          cancelButtonText: "Bekor qilish",
          type: "warning",
        }
      )
        .then(() => {
          this.loading = true;
          this.$http
            .delete(`api/v1/deputyv2/okrugs/${this.okrugId}/quarters/${quarter.quarter_id}`)
            .then(() => {
              ElNotification({
                type: "success",
                title: "Bajarildi",
                message: "Mahalla uzildi",
              });
              this.getOkrug();
            })
            .catch(({ response }) => {
              ElNotification({
                type: "error",
                title: "Xatolik",
                message: response?.data?.message || "Iltimos, qayta urinib ko'ring",
              });
              this.loading = false;
            });
        })
        .catch(() => {});
    },
  },
  mounted() {
    this.getRegions();
    this.getOkrug();
  },
};
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.page-header__title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 600;
}

.form-section {
  border: 1px solid #dcdfe6;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  background: #fff;
}

.form-section__title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
}

.field-label {
  font-size: 14px;
  color: #606266;
  margin-bottom: 6px;
}

.linked-hint {
  float: right;
  color: #909399;
  font-size: 12px;
}

.section-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.group {
  margin-bottom: 18px;
}

.group__region {
  font-weight: 700;
  font-size: 15px;
  color: #007791;
  margin-bottom: 8px;
}

.group__district {
  padding-left: 12px;
  border-left: 3px solid #409eff;
  margin-bottom: 12px;
}

.group__district-title {
  font-weight: 600;
  color: #337ecc;
  margin-bottom: 8px;
}

.quarter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.quarter-tag {
  margin: 0;
}

.quarter-tag__count {
  color: #909399;
  font-size: 12px;
}
</style>
